import { Button, Collapse, Colors, Intent, Tag } from '@blueprintjs/core'
import * as React from 'react'
import NotesRenderer from '../../../components/Notes'
import { Note } from '../../../types/api'
import './style.scss'
import { UprightStyle } from '../../../types'

const styles: UprightStyle = {
  emphaticBorder: { border: '1px solid', borderColor: Colors.ORANGE3 },
  notesWrapper: { padding: 20 }
}

interface Props {
  productId: number
  initialNoteCount?: number
}

const isNotesCollapsed = (initialNoteCount: number): boolean => {
  return !(initialNoteCount > 0)
}

export const ProductNotes = (props: Props): JSX.Element => {
  const [noteCount, setNoteCount] = React.useState<number>(props.initialNoteCount || 0)
  const [sectionCollapsed, setSectionCollapsed] = React.useState(() => isNotesCollapsed(props.initialNoteCount))
  const toggleSectionCollapse = () => {
    const newSectionCollapsed = !sectionCollapsed
    setSectionCollapsed(newSectionCollapsed)
  }

  const collapseButtonText = React.useMemo(() => {
    // if no notes exist
    if (sectionCollapsed && noteCount === 0) return 'Add a note'

    // if already has notes
    if (sectionCollapsed) return 'Show notes'

    // component is not collapsed
    return 'Hide'
  }, [sectionCollapsed, noteCount])

  const onUpdateNotes = React.useCallback((notes: Note[]) => setNoteCount(notes.length), [])

  React.useEffect(() => {
    setNoteCount(props.initialNoteCount)
    setSectionCollapsed(isNotesCollapsed(props.initialNoteCount))
  }, [props.productId, props.initialNoteCount])

  return (
    <div style={{ ...styles.notesWrapper, ...(noteCount > 0 ? styles.emphaticBorder : null) }}>
      <div className="notes-header">
        <div className="notes-header-left">
          <h3>Notes</h3>
          <Tag minimal round>
            {noteCount}
          </Tag>
        </div>
        <Button minimal intent={Intent.PRIMARY} onClick={toggleSectionCollapse} text={collapseButtonText} />
      </div>
      <Collapse isOpen={!sectionCollapsed} keepChildrenMounted>
        <div className="notes-body">
          <NotesRenderer
            resource="Product"
            onUpdateNotes={onUpdateNotes}
            resourceId={props.productId || 0}
            allowNewOrEditNotes
          />
        </div>
      </Collapse>
    </div>
  )
}
